import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "../../context/CartContext";
import { useCoupon } from "../../context/CouponContext";
import { useWishlist } from "../../context/WishlistContext";
import EmptyState from "../../components/common/EmptyState";
import ShippingAddressForm from "../../components/checkout/ShippingAddressForm";
import PaymentSection from "../../components/checkout/PaymentSection";
import OrderSummary from "../../components/checkout/OrderSummary";
import WishlistModal from "../../components/checkout/WishlistModal";
import { buildCheckoutPayload, createOrder } from "../../services/orderService";
import { initiatePhonePePayment } from "../../services/paymentService";

const initialCheckoutForm = {
  full_name: "",
  email: "",
  phone: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  pincode: "",
  country: "India",
};

const productFromCartItem = (item) => ({
  id: item.product_id,
  slug: item.slug,
  name: item.name,
  price: item.price,
  original_price: item.original_price,
  images: item.image ? [item.image] : [],
  colors: item.color ? [item.color] : [],
  sizes: item.size ? [item.size] : [],
  qikink_sku: item.sku,
  qikink_designs: item.variant_details?.qikink_designs || [],
  color_images: item.color && item.variant_details?.selected_color_images?.length
    ? { [item.color]: item.variant_details.selected_color_images }
    : {},
  active_status: 1,
});

const CheckoutPageContent = () => {
  const {
    items,
    subtotal,
    discount,
    total,
    clear,
    removeItem,
    addItem,
    updateQty,
    updateItemVariant,
    pendingKeys: cartPendingKeys,
  } = useCart();
  const { appliedCoupon, discountAmount, removeCoupon, revalidateCoupon } = useCoupon();
  const {
    items: wishlistItems,
    loading: wishlistLoading,
    pendingKeys: wishlistPendingKeys,
    addItem: addToWishlist,
  } = useWishlist();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [form, setForm] = useState(initialCheckoutForm);
  const [wishlistModalOpen, setWishlistModalOpen] = useState(false);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const couponDiscount = useMemo(() => {
    const data = appliedCoupon?.data;
    if (!data) return discountAmount;
    const value = Number(data.discount_value || 0);
    if (data.discount_type === "percentage") return Math.min(subtotal, (subtotal * value) / 100);
    if (data.discount_type === "fixed") return Math.min(subtotal, value);
    return discountAmount;
  }, [appliedCoupon, discountAmount, subtotal]);
  const totalWithCoupon = Math.max(0, total - couponDiscount);

  useEffect(() => {
    if (!appliedCoupon?.code || items.length === 0) return undefined;
    const timer = window.setTimeout(() => {
      revalidateCoupon(appliedCoupon.code, subtotal, null, items, form.email || null);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [appliedCoupon?.code, form.email, items, revalidateCoupon, subtotal]);

  const handleRemoveItem = async (productId, size, color = "") => {
    const removed = await removeItem(productId, size, color);
    if (removed) toast.success("Removed from cart");
  };

  const handleUpdateQuantity = async (productId, size, color, newQuantity) => {
    if (newQuantity < 1) return;
    await updateQty(productId, size, color, newQuantity);
  };

  const handleMoveToWishlist = async (item) => {
    const saved = await addToWishlist(
      productFromCartItem(item),
      item.color || "",
      item.size || "",
      { notify: false }
    );
    if (!saved) return;

    const removed = await removeItem(item.product_id, item.size, item.color || "");
    if (removed) toast.success("Item saved to wishlist");
  };

  const handleVariantChange = async (item, nextSize, nextColor) => {
    await updateItemVariant(item.product_id, item.size, item.color || "", nextSize, nextColor);
  };

  const handleAddWishlistItemToCart = async (wishlistItem) => {
    const product = wishlistItem.product;
    if (!product?.id) {
      toast.error("This wishlist item is no longer available.");
      return false;
    }

    const size = wishlistItem.selectedSize || product.sizes?.[0] || "M";
    const color = wishlistItem.selectedColor || product.colors?.[0] || "";
    const added = await addItem(product, size, 1, color, true);
    if (!added) {
      toast.error("Could not add item from wishlist. Please try again.");
      return false;
    }

    toast.success("Added to cart successfully");
    return true;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (items.length === 0) return;
    setError("");
    setSubmitting(true);
    try {
      const payload = buildCheckoutPayload({
        form,
        items,
        paymentMethod,
        discountCode: appliedCoupon ? appliedCoupon.code : "",
      });
      if (paymentMethod === "phonepe") {
        const data = await initiatePhonePePayment(payload);
        window.location.href = data.payment_url;
        return;
      }
      const order = await createOrder(payload);
      removeCoupon(true);
      await clear();
      navigate(`/order/${order.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not place order. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const wishlistModal = (
    <WishlistModal
      open={wishlistModalOpen}
      onClose={() => setWishlistModalOpen(false)}
      items={wishlistItems}
      loading={wishlistLoading}
      pendingKeys={wishlistPendingKeys}
      onAddToCart={handleAddWishlistItemToCart}
    />
  );

  if (items.length === 0) {
    return (
      <>
        <div className="mx-auto max-w-lg px-6 pt-32" data-testid="checkout-empty">
          <EmptyState title="Your cart is empty." message="Browse the latest drops or add a saved item without leaving checkout." />
          <div className="mt-8 space-y-3">
            <button
              onClick={() => setWishlistModalOpen(true)}
              className="w-full bg-cynos-red px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition hover:bg-red-600"
              data-testid="checkout-empty-wishlist"
            >
              Add from wishlist
            </button>
            <button
              onClick={() => navigate("/shop")}
              className="w-full border border-white/30 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition hover:border-white/60 hover:bg-white/5"
              data-testid="back-to-shop-btn"
            >
              Continue shopping
            </button>
          </div>
        </div>
        {wishlistModal}
      </>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-cynos-surface px-4 pb-32 pt-20 md:px-6 md:pb-24 md:pt-24 lg:px-8" data-testid="checkout-page">
        <div className="mx-auto max-w-7xl">
          <h1 className="mb-2 font-anton text-5xl uppercase md:text-7xl">Checkout.</h1>
          <p className="mb-8 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 md:mb-10">Guest checkout · no account needed</p>

          <form onSubmit={onSubmit} className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-12 lg:gap-16">
            <div className="space-y-8">
              <ShippingAddressForm form={form} onUpdate={update} />
              <PaymentSection paymentMethod={paymentMethod} onPaymentMethodChange={setPaymentMethod} />
            </div>

            <OrderSummary
            items={items}
            subtotal={subtotal}
            discount={discount}
            couponDiscount={couponDiscount}
            total={totalWithCoupon}
            error={error}
            submitting={submitting}
            paymentMethod={paymentMethod}
            customerEmail={form.email}
            pendingKeys={cartPendingKeys}
            onRemoveItem={handleRemoveItem}
            onMoveToWishlist={handleMoveToWishlist}
            onUpdateQuantity={handleUpdateQuantity}
            onVariantChange={handleVariantChange}
            onAddFromWishlist={() => setWishlistModalOpen(true)}
          />
          </form>
        </div>
      </div>
      {wishlistModal}
    </>
  );
};

const CheckoutPage = () => <CheckoutPageContent />;

export default CheckoutPage;
